/**
 * Nexus theme system — light / dark / system + 5 accent colors + neon glow.
 *
 * Every color anywhere in the app is derived from CSS variables set on
 * <html>. Components only read `hsl(var(--accent))` etc, never a
 * hard-coded hex. Persisted in localStorage so the user's choice
 * survives reloads and cross-tab syncs via the storage event.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

export type ThemeMode = 'dark' | 'light' | 'system';
export type Accent = 'turquesa' | 'azul' | 'verde' | 'morado' | 'rosa' | 'naranja';
export type BgVariant = 'oled' | 'graphite';

/** HSL triples so we can `hsl(var(--accent))` and also `hsl(var(--accent) / 0.15)`. */
const ACCENT_HSL: Record<Accent, string> = {
  turquesa: '186 100% 55%',
  azul:     '211 92% 60%',
  verde:    '142 82% 55%',
  morado:   '265 84% 68%',
  rosa:     '335 82% 62%',
  naranja:  '18 92% 62%',
};

/** Background variants (v1.16 · point #4). */
const BG_HSL: Record<BgVariant, { bg: string; bgElev: string }> = {
  oled:     { bg: '215 55% 3%',  bgElev: '218 33% 5%' },
  graphite: { bg: '220 12% 7%',  bgElev: '220 12% 11%' },
};

interface Ctx {
  mode: ThemeMode;
  accent: Accent;
  bg: BgVariant;
  neon: boolean;
  setMode: (m: ThemeMode) => void;
  setAccent: (a: Accent) => void;
  setBg: (b: BgVariant) => void;
  setNeon: (n: boolean) => void;
  /** Currently-effective 'dark' or 'light' (system resolved). */
  resolved: 'dark' | 'light';
}

const ThemeCtx = createContext<Ctx | null>(null);

const LS = {
  mode: 'nexus_theme_mode',
  accent: 'nexus_theme_accent',
  bg: 'nexus_theme_bg',
  neon: 'nexus_theme_neon',
};

function resolve(mode: ThemeMode): 'dark' | 'light' {
  if (mode === 'system') {
    return typeof window !== 'undefined' &&
      window.matchMedia('(prefers-color-scheme: light)').matches
      ? 'light'
      : 'dark';
  }
  return mode;
}

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mode, setModeState] = useState<ThemeMode>(
    () => (localStorage.getItem(LS.mode) as ThemeMode) || 'dark',
  );
  const [accent, setAccentState] = useState<Accent>(
    () => (localStorage.getItem(LS.accent) as Accent) || 'turquesa',
  );
  const [bg, setBgState] = useState<BgVariant>(
    () => (localStorage.getItem(LS.bg) as BgVariant) || 'oled',
  );
  const [neon, setNeonState] = useState<boolean>(
    () => localStorage.getItem(LS.neon) !== 'false', // default ON — matches reference
  );
  const [resolved, setResolved] = useState<'dark' | 'light'>(() => resolve(mode));

  useEffect(() => {
    const root = document.documentElement;
    const eff = resolve(mode);
    setResolved(eff);
    root.dataset.theme = eff;
    root.dataset.accent = accent;
    root.dataset.bg = bg;
    root.dataset.neon = neon ? 'on' : 'off';
    root.style.setProperty('--accent', ACCENT_HSL[accent]);
    root.style.setProperty('--bg', BG_HSL[bg].bg);
    root.style.setProperty('--bg-elev', BG_HSL[bg].bgElev);
    root.classList.toggle('dark', eff === 'dark');
  }, [mode, accent, bg, neon]);

  useEffect(() => {
    if (mode !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: light)');
    const handler = () => setResolved(mq.matches ? 'light' : 'dark');
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [mode]);

  const setMode = useCallback((m: ThemeMode) => {
    localStorage.setItem(LS.mode, m);
    setModeState(m);
  }, []);
  const setAccent = useCallback((a: Accent) => {
    localStorage.setItem(LS.accent, a);
    setAccentState(a);
  }, []);
  const setBg = useCallback((b: BgVariant) => {
    localStorage.setItem(LS.bg, b);
    setBgState(b);
  }, []);
  const setNeon = useCallback((n: boolean) => {
    localStorage.setItem(LS.neon, String(n));
    setNeonState(n);
  }, []);

  const value = useMemo(
    () => ({ mode, accent, bg, neon, setMode, setAccent, setBg, setNeon, resolved }),
    [mode, accent, bg, neon, setMode, setAccent, setBg, setNeon, resolved],
  );
  return <ThemeCtx.Provider value={value}>{children}</ThemeCtx.Provider>;
};

export function useTheme(): Ctx {
  const ctx = useContext(ThemeCtx);
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>');
  return ctx;
}

export const ACCENTS: { id: Accent; label: string; hex: string }[] = [
  { id: 'turquesa', label: 'Turquesa',      hex: '#00F0FF' },
  { id: 'azul',     label: 'Azul eléctrico', hex: '#4c9dfa' },
  { id: 'verde',    label: 'Verde neón',    hex: '#22d97a' },
  { id: 'morado',   label: 'Morado',        hex: '#a97af6' },
  { id: 'rosa',     label: 'Rosa',          hex: '#ee5c94' },
  { id: 'naranja',  label: 'Naranja',       hex: '#f78e4a' },
];

export const BG_VARIANTS: { id: BgVariant; label: string; hex: string }[] = [
  { id: 'oled',     label: 'Negro profundo',        hex: '#030407' },
  { id: 'graphite', label: 'Gris oscuro elegante',  hex: '#141821' },
];
